import type { GetStaticPropsContext } from 'next'

import DrillTopicListElementRenderer from '~/components/Drill/DrillTopicsList/components/DrillTopicListElementRenderer'
import LianaDesktop from '~/components/Drill/DrillTopicsList/components/LianaDesktop'
import LianaMobile from '~/components/Drill/DrillTopicsList/components/LianaMobile'
import LianaTablet from '~/components/Drill/DrillTopicsList/components/LianaTablet'
import { DEFAULT_LAYOUT_TRANSLATION_MODULE_NAMES } from '~/shared/layouts/constants'
import { DefaultLayout } from '~/shared/layouts/DefaultLayout'
import { DRILL_TOPICS } from '~/shared/constants/drill.const'
import { usePageTitle } from '~/utils/tags'
import Metatags from '~/components/Metadata/Metatags'
import { getTopicData } from '~/../utils/data-fetch/theory/theory.query'
import { AppLanguage } from '~/types/locale'
import { Breakpoint, useBreakpoint } from '~/utils'
import { getStaticPropsWrapper } from '~/utils/pageWrapper/staticPageWrapper'
import { GetProps } from '~/utils/pageWrapper/types'

export default function Drill({
  topics,
  title,
  metaDescription,
  h1,
}: {
  topics: Array<{ path: string; title: string }>
  title: string
  metaDescription: string
  h1: string
}): React.ReactElement {
  const breakpoint = useBreakpoint()
  return (
    <DefaultLayout>
      <Metatags metaDescription={metaDescription} title={usePageTitle(title)} />

      <h1 className="z-10 mb-16 md:mb-32 xl:mb-10 text-center text-h3 md:text-h1 font-semibold text-white">{h1}</h1>

      <div className="max-w-max mx-auto">
        <div className="relative">
          {breakpoint < Breakpoint.MD && <LianaMobile />}
          {breakpoint >= Breakpoint.MD && breakpoint < Breakpoint.XL && <LianaTablet />}
          {breakpoint >= Breakpoint.XL && <LianaDesktop />}
          <div className="grid grid-cols-3 md:grid-cols-6 xl:grid-cols-8 grid-rows-[repeat(18,1fr)] md:grid-rows-4 xl:grid-rows-3">
            {topics?.map(({ path, title: topicTitle }) => (
              <DrillTopicListElementRenderer key={path} path={path} title={topicTitle} />
            ))}
          </div>
        </div>
      </div>
    </DefaultLayout>
  )
}

export const getProps: GetProps =
  () =>
  async ({ locale }: GetStaticPropsContext<{ locale: string }>) => {
    const index = locale === 'en' ? 0 : locale === 'no' ? 1 : 2
    const topics = Object.values(DRILL_TOPICS).map((topic) => topic[index])
    const drillPaths = ['drill']
    const topicData = await getTopicData({
      paths: drillPaths,
      language: locale as AppLanguage,
    })

    if (!topicData || !('topic' in topicData)) {
      return {
        notFound: true,
      }
    }

    const topic = topicData.topic
    return {
      props: {
        title: topic.title,
        metaDescription: topic.metaDescription,
        h1: topic.h1,
        topics,
        namespaces: ['bootcamp', 'drill', 'multipleChoice', ...DEFAULT_LAYOUT_TRANSLATION_MODULE_NAMES],
      },
    }
  }

export const getStaticProps = getStaticPropsWrapper(getProps)
